import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import api from '@/utils/ky';
import { motion } from 'framer-motion';
import { Download, FileText, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FeedbackAudioControls } from './components/FeedbackAudioControls';
import { FeedbackTabs } from './components/FeedbackTabs';
import { SkillAssessmentCard } from './components/SkillAssessmentCard';
import { TranscriptViewer } from './components/TranscriptViewer';

interface CustomerFeedbackProps {
  sessionId: string;
  feedback: any;
  onPracticeAgain: () => void;
  onDiscussFeedback: () => void;
}

export default function CustomerFeedback({
  sessionId,
  feedback,
  onPracticeAgain,
  onDiscussFeedback,
}: CustomerFeedbackProps) {
  const [feedbackData, setFeedbackData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'service' | 'brand'>(
    'overview',
  );

  // PDF states
  const [pdfState, setPdfState] = useState<
    'checking' | 'available' | 'generating' | 'error'
  >('checking');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // TTS states
  const [isTtsLoading, setIsTtsLoading] = useState(false);
  const [isTtsPlaying, setIsTtsPlaying] = useState(false);
  const [isTtsPaused, setIsTtsPaused] = useState(false);
  const [ttsError, setTtsError] = useState<string | null>(null);

  // TTS refs
  const ttsSocketRef = useRef<WebSocket | null>(null);
  const ttsAudioRef = useRef<HTMLAudioElement | null>(null);
  const ttsFeedbackRef = useRef<string | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioUrlRef = useRef<string | null>(null);

  // Initialize WebSocket for TTS
  useEffect(() => {
    setupTtsWebSocket();

    return () => {
      if (ttsSocketRef.current) {
        ttsSocketRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
    };
  }, []);

  // Initialize feedback data
  useEffect(() => {
    if (feedback) {
      setFeedbackData(feedback);
      setIsLoading(false);
      
      // Check TTS availability
      checkTTSFeedback();
      
      // Check PDF availability
      checkPDFAvailability();
    } else {
      setError('No feedback data provided');
      setIsLoading(false);
    }
  }, [feedback]);

  // Interface for TTS feedback response
  interface TTSFeedbackResponse {
    exists: boolean;
  }

  const checkTTSFeedback = async () => {
    try {
      const response = await api
        .get(`api/customer/sessions/${sessionId}/tts-feedback`)
        .json<TTSFeedbackResponse>();
      if (!response.exists) {
        // Generate TTS feedback in background
        generateTTSFeedback();
      }
    } catch (err) {
      console.error('Failed to check TTS feedback:', err);
      // Don't show error to user as TTS is optional feature
    }
  };

  const generateTTSFeedback = async () => {
    try {
      await api
        .post(`api/customer/sessions/${sessionId}/generate-tts-feedback`)
        .json();
    } catch (err) {
      console.error('Failed to generate TTS feedback:', err);
      // Don't show error to user as TTS is optional feature
    }
  };

  const checkPDFAvailability = async () => {
    try {
      const response: any = await api
        .get(`api/customer/sessions/${sessionId}/pdf`)
        .json();
      if (response.exists) {
        setPdfUrl(response.url);
        setPdfState('available');
      } else {
        setPdfState('checking');
      }
    } catch (err) {
      setPdfState('error');
    }
  };

  const handleGeneratePDF = async () => {
    setPdfState('generating');
    try {
      const response: any = await api
        .post(`api/customer/sessions/${sessionId}/generate-pdf`, {
          json: { userName: feedbackData?.practiceInfo?.guest || 'User' },
        })
        .json();

      setPdfUrl(response.pdfUrl);
      setPdfState('available');
      window.open(response.pdfUrl, '_blank');
    } catch (err) {
      setPdfState('error');
    }
  };

  const setupTtsWebSocket = () => {
    const wsUrl =
      import.meta.env.VITE_TTS_WEBSOCKET_URI || 'ws://localhost:5002';

    try {
      const ws = new WebSocket(wsUrl);
      ttsSocketRef.current = ws;

      ws.onopen = () => {
        console.log('TTS WebSocket connected');
        setTtsError(null);
      };

      ws.onclose = () => {
        console.log('TTS WebSocket disconnected');
        ttsSocketRef.current = null;
      };

      ws.onerror = (error) => {
        console.error('TTS WebSocket error:', error);
        setTtsError('Failed to connect to audio service');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.audioContent) {
            // Convert base64 to blob and play
            const audioBlob = base64ToBlob(data.audioContent, 'audio/mp3');
            const url = URL.createObjectURL(audioBlob);

            if (audioUrlRef.current) {
              URL.revokeObjectURL(audioUrlRef.current);
            }
            audioUrlRef.current = url;

            const audio = new Audio(url);
            ttsAudioRef.current = audio;

            audio.onended = () => {
              setIsTtsPlaying(false);
              setIsTtsPaused(false);
            };

            audio.play();
            setIsTtsPlaying(true);
            setIsTtsLoading(false);
          }
        } catch (error) {
          console.error('Error processing TTS message:', error);
        }
      };
    } catch (error) {
      console.error('Error setting up TTS WebSocket:', error);
      setTtsError('Failed to initialize audio service');
    }
  };

  const base64ToBlob = (base64: string, mimeType: string) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  const handlePlayFeedback = async () => {
    if (!feedbackData || isTtsLoading || isTtsPlaying) return;

    try {
      setIsTtsLoading(true);
      setTtsError(null);

      // Check if TTS feedback text exists
      const ttsFeedbackResponse: any = await api
        .get(`api/customer/sessions/${sessionId}/tts-feedback`)
        .json();

      if (!ttsFeedbackResponse.exists) {
        // Generate TTS feedback first
        await api
          .post(`api/customer/sessions/${sessionId}/generate-tts-feedback`)
          .json();
        const updatedResponse: any = await api
          .get(`api/customer/sessions/${sessionId}/tts-feedback`)
          .json();
        ttsFeedbackRef.current = updatedResponse.text;
      } else {
        ttsFeedbackRef.current = ttsFeedbackResponse.text;
      }

      // Send to WebSocket for audio generation
      if (
        ttsSocketRef.current?.readyState === WebSocket.OPEN &&
        ttsFeedbackRef.current
      ) {
        ttsSocketRef.current.send(
          JSON.stringify({
            text: ttsFeedbackRef.current,
            languageCode: 'en-US',
          }),
        );
      } else {
        throw new Error('Audio service not connected');
      }
    } catch (error: any) {
      console.error('Error playing feedback:', error);
      setTtsError(error.message || 'Failed to play feedback');
      setIsTtsLoading(false);
    }
  };

  const handlePauseFeedback = () => {
    if (ttsAudioRef.current && isTtsPlaying) {
      ttsAudioRef.current.pause();
      setIsTtsPaused(true);
    }
  };

  const handleResumeFeedback = () => {
    if (ttsAudioRef.current && isTtsPaused) {
      ttsAudioRef.current.play();
      setIsTtsPaused(false);
    }
  };

  const generateFeedbackText = () => {
    if (!feedbackData) return '';

    const { assessment, skills } = feedbackData;

    return `
      Your overall score is ${assessment.overallScore} out of 100.

      ${assessment.summary}

      In Service Delivery, you scored ${
        skills.serviceDelivery.score
      } out of 100.
      ${skills.serviceDelivery.subSkills.tone.why}
      ${skills.serviceDelivery.subSkills.tone.suggestion}

      In Brand Alignment, you scored ${skills.brandAlignment.score} out of 100.

      Your suggested next steps are:
      ${assessment.suggestedNextSteps.join('. ')}
    `;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">
            Loading your feedback...
          </h3>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !feedbackData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            {error || 'Failed to load feedback'}
          </p>
          <Button onClick={onPracticeAgain}>Try Again</Button>
        </div>
      </div>
    );
  }

  console.log('feedbackData:', feedbackData);

  const { assessment, skills, transcript, practiceInfo } = feedbackData;

  if (!feedbackData || !practiceInfo) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            {error || 'Failed to load feedback'}
          </p>
          <Button onClick={onPracticeAgain}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Roleplay Session Assessment
            </h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
              <span>{practiceInfo.type}</span>
              <span>•</span>
              <span>{practiceInfo.guest}</span>
              <span>•</span>
              <span>Completed {practiceInfo.completedAt}</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3">
            {ttsSocketRef.current && (
              <FeedbackAudioControls
                isPlaying={isTtsPlaying}
                isPaused={isTtsPaused}
                isLoading={isTtsLoading}
                onPlay={handlePlayFeedback}
                onPause={handlePauseFeedback}
                onResume={handleResumeFeedback}
              />
            )}

            <Button
              variant="outline"
              onClick={handleGeneratePDF}
              disabled={pdfState === 'generating'}
              className="flex items-center gap-2"
            >
              {pdfState === 'generating' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating PDF...
                </>
              ) : pdfState === 'available' ? (
                <>
                  <Download className="w-4 h-4" />
                  Download PDF
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  Generate PDF
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Overall Score Card */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Overall Score
              </h2>
              <p className="text-gray-600 max-w-3xl">{assessment.summary}</p>
            </div>
            <div className="relative w-32 h-32">
              <svg className="w-32 h-32 transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  className="text-gray-200"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${
                    (2 * Math.PI * 56 * assessment.overallScore) / 100
                  } ${2 * Math.PI * 56}`}
                  className={cn(
                    assessment.overallScore >= 70
                      ? 'text-green-500'
                      : assessment.overallScore >= 40
                      ? 'text-yellow-500'
                      : 'text-red-500',
                  )}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold">
                  {assessment.overallScore}
                </span>
                <span className="text-sm text-gray-500">out of 100</span>
              </div>
            </div>
          </div>
        </div>

        {/* Suggested Next Steps */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            Suggested Next Steps
          </h3>
          <ul className="space-y-2">
            {assessment.suggestedNextSteps.map(
              (step: string, index: number) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">{index + 1}.</span>
                  <span className="text-blue-800">{step}</span>
                </li>
              ),
            )}
          </ul>
        </div>
      </motion.div>

      {/* Tabs */}
      <FeedbackTabs activeTab={activeTab} onTabChange={setActiveTab}>
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Service Delivery Overview */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-xl font-semibold mb-4">Service Delivery</h3>
              <p className="text-gray-600 mb-6">
                {skills.serviceDelivery.description}
              </p>
              <div className="text-3xl font-bold text-center mb-4">
                {skills.serviceDelivery.score} / 100
              </div>
            </div>

            {/* Brand Alignment Overview */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-xl font-semibold mb-4">Brand Alignment</h3>
              <p className="text-gray-600 mb-6">
                {skills.brandAlignment.description}
              </p>
              <div className="text-3xl font-bold text-center mb-4">
                {skills.brandAlignment.score} / 100
              </div>
            </div>

            {/* Transcript */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-xl font-semibold mb-4">
                Conversation Transcript
              </h3>
              <TranscriptViewer transcript={transcript} />
            </div>
          </motion.div>
        )}

        {activeTab === 'service' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <div className="mb-6">
              <h3 className="text-xl font-semibold">
                Service Delivery Analysis
              </h3>
              <p className="text-gray-600 mt-2">
                {skills.serviceDelivery.description}
              </p>
              <div className="text-2xl font-bold mt-4">
                Overall: {skills.serviceDelivery.score} / 100
              </div>
            </div>

            {Object.entries(skills.serviceDelivery.subSkills).map(
              ([key, skill]: [string, any]) => (
                <SkillAssessmentCard
                  key={key}
                  title={skill.label}
                  label={skill.label}
                  score={skill.score}
                  maxScore={skill.maxScore}
                  why={skill.why}
                  suggestion={skill.suggestion}
                />
              ),
            )}
          </motion.div>
        )}

        {activeTab === 'brand' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <div className="mb-6">
              <h3 className="text-xl font-semibold">
                Brand Alignment Analysis
              </h3>
              <p className="text-gray-600 mt-2">
                {skills.brandAlignment.description}
              </p>
              <div className="text-2xl font-bold mt-4">
                Overall: {skills.brandAlignment.score} / 100
              </div>
            </div>

            {Object.entries(skills.brandAlignment.subSkills).map(
              ([key, skill]: [string, any]) => (
                <SkillAssessmentCard
                  key={key}
                  title={skill.label}
                  label={skill.label}
                  score={skill.score}
                  maxScore={skill.maxScore}
                  why={skill.why}
                  suggestion={skill.suggestion}
                  strengths={skill.strengths}
                  toImprove={skill.toImprove}
                  note={skill.note}
                />
              ),
            )}
          </motion.div>
        )}
      </FeedbackTabs>

      {/* Action Buttons */}
      <div className="flex justify-center gap-4 mt-12">
        <Button
          variant="outline"
          size="lg"
          onClick={onPracticeAgain}
          className="min-w-[200px]"
        >
          Practice Again
        </Button>
        <Button size="lg" onClick={onDiscussFeedback} className="min-w-[200px]">
          Discuss Feedback
        </Button>
      </div>
    </div>
  );
}
